import { useEffect, useState } from 'react';
import { getCurrentUser, getCollection, getDocument, storage } from '../../firebase';
import { Page, BlockTitle,
  Link,
  Actions,
  ActionsGroup,
  ActionsLabel,
  ActionsButton,
  Popup,
  Navbar
} from 'framework7-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import '@styles/educationalVideosList.css';

import ReactPlayer from 'react-player';
import { Video } from '../../models/Video';
import { ref, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../../hooks/useAuth';
import AccessRestricted from '../AccessRestricted';
import '@styles/educationalVideosList.css';

import SearchIcon from '@assets/icons/search.svg?react';
import SortIcon from '@assets/icons/ic_order.svg?react';
import CloseEllipse from '@assets/icons/Close-ellipse.svg?react';
import CloseSearch from '@assets/icons/close-search.svg?react';

function EducationalVideosList() {
  const { isAuthenticated } = useAuth(false);
  const [firstName, setFirstName] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [videos, setVideos] = useState<Video[]>([]);

  const [actionsOpened, setActionsOpened] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);
  const [popupOpened, setPopupOpened] = useState(false);

  useEffect(() => {
    const fetchName = async () => {
      const user = await getCurrentUser();
      if (!user) return;

      let name = '';
      try {
        const snap = await getDocument(`users/${user.uid}`);
        if (snap.exists()) {
          const data = snap.data() as { fullName?: string };
          if (data.fullName) {
            name = data.fullName.split(' ')[0];
          }
        }
      } catch (err) {
        console.error('Error fetching user name', err);
      }

      if (!name) {
        const email = user.email;
        const displayName = user.displayName;
        if (displayName) name = displayName.split(' ')[0];
        else if (email) name = email.split('@')[0];
      }

      if (name) setFirstName(name);
    };

    fetchName();
  }, []);
  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const snap = await getCollection('videos');
        const loaded: Video[] = [];
        for (const docSnap of snap.docs) {
          const data = docSnap.data() as Partial<Video> & { storagePath?: string };
          let url = data.url;
          if (!url && data.storagePath) {
            url = await getDownloadURL(ref(storage, data.storagePath));
          }
          loaded.push({
            id: docSnap.id,
            title: data.title || '',
            url: url || '',
            thumbnailUrl: data.thumbnailUrl || '',
            description: data.description || '',
            featured: data.featured || false,
            publishedAt: data.publishedAt,
          });
        }
        setVideos(loaded);
      } catch (err) {
        console.error('Error loading videos', err);
      }
    };
    fetchVideos();
  }, []);

  const filteredVideos = videos.filter((video) =>
    video.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    video.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const isSearching = searchTerm.trim() !== '';

  const featuredVideos = videos.filter((v) => v.featured);
  const today = new Date();
  const july15 = new Date(today.getFullYear(), 7, 31);
  const showPlaceholder =
    !isSearching && videos.length === 0 && today < july15;

  const formatDate = (iso?: string | null) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });
  };

  return (
    <Page name="educational-videos-list">
      <div className="search-wrapper">
        <SearchIcon className="search-icon" />
        <input
          type="text"
          placeholder="Search for a video"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        {searchTerm && (
          <button
            type="button"
            className="clear-search"
            onClick={() => setSearchTerm('')}
          >
            <CloseSearch />
          </button>
        )}
      </div>

      {!isAuthenticated ? (
        <AccessRestricted />
      ) : (
        <div className="video-list-page-content">
          {!isSearching && (
            <div className="user-header">
              <h2 className="greeting-title">Hi {firstName}</h2>
              <p className="greeting-subtitle">
                You have {filteredVideos.length} video lessons to view.
              </p>
            </div>
          )}

          {!isSearching && featuredVideos.length > 0 && (
            <>
              <div className="videos-header">
                <BlockTitle className="section-title">Featured</BlockTitle>
              </div>

              <div className="featured-section">
                <Swiper
                  spaceBetween={16}
                  slidesPerView={1.1}
                  style={{ paddingLeft: 16 }}
                >
                  {featuredVideos.map((video, index) => (
                    <SwiperSlide key={index}>
                      <div
                        className="featured-video-card"
                        onClick={() => {
                          setSelectedVideo(video);
                          setPopupOpened(true);
                        }}
                      >
                        <div className="featured-thumbnail-wrapper">
                          <img
                            src={video.thumbnailUrl}
                            alt={video.title}
                            className="featured-thumbnail"
                          />
                          <i className="f7-icons play-overlay">play_circle_fill</i>
                        </div>
                        <div className="featured-info">
                          <div className="featured-title">{video.title}</div>
                          <div className="featured-description">{video.description}</div>
                          {formatDate(video.publishedAt) && (
                            <div className="featured-meta">{formatDate(video.publishedAt)}</div>
                          )}
                        </div>
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
            </>
          )}

          {filteredVideos.length > 0 && (
            <div className="videos-header">
              <BlockTitle className="section-title">
                {isSearching ? 'Results' : 'All videos'}
              </BlockTitle>
              {!isSearching && (
                <SortIcon
                  onClick={() => setActionsOpened(true)}
                />
              )}
              <Actions opened={actionsOpened} onActionsClosed={() => setActionsOpened(false)}>
                <ActionsGroup>
                  <ActionsLabel>Sort video titles by</ActionsLabel>
                  <ActionsButton onClick={() => setActionsOpened(false)}>Most popular</ActionsButton>
                  <ActionsButton onClick={() => setActionsOpened(false)}>Newest first</ActionsButton>
                  <ActionsButton onClick={() => setActionsOpened(false)}>Oldest first</ActionsButton>
                </ActionsGroup>
                <ActionsGroup>
                  <ActionsButton color="red" onClick={() => setActionsOpened(false)}>Cancel</ActionsButton>
                </ActionsGroup>
              </Actions>
            </div>
          )}

          {showPlaceholder ? (
            <div className="no-results-wrapper">
              <div className="no-results-title">First video coming August 31</div>
            </div>
          ) : filteredVideos.length === 0 && isSearching ? (
            <div className="no-results-wrapper">
              <CloseEllipse className="no-results-icon" />
              <div className="no-results-title">Nothing found</div>
              <div className="no-results-subtitle">Try searching a different keyword</div>
            </div>
          ) : (
            filteredVideos.map((video) => (
              <div
                key={video.id}
                className="video-list-item"
                onClick={() => {
                  setSelectedVideo(video);
                  setPopupOpened(true);
                }}
              >
                <div className="video-thumbnail-wrapper">
                  <img
                    src={video.thumbnailUrl}
                    alt={video.title}
                    className="video-thumbnail"
                  />
                  <i className="f7-icons play-overlay small">arrowtriangle_right_circle_fill</i>
                </div>
                <div className="video-info">
                  <div className="video-title">{video.title}</div>
                  {formatDate(video.publishedAt) && (
                    <div className="video-meta">{formatDate(video.publishedAt)}</div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      <Popup
        className="video-popup"
        tabletFullscreen
        opened={popupOpened}
        onPopupClosed={() => {
          setPopupOpened(false);
          setSelectedVideo(null);
        }}
        closeByBackdropClick
      >
        <Page>
          <Navbar title="Now Playing">
            <Link slot="right" popupClose>Close</Link>
          </Navbar>
          <div className="page-content">
            {selectedVideo && (
              <div className="player-container">
                <ReactPlayer
                  url={selectedVideo.url}
                  playing
                  controls
                  width="100%"
                  height="100%"
                  playsinline
                  config={{
                    youtube: {
                      playerVars: {
                        modestbranding: 1,
                        rel: 0,
                        playsinline: 1,
                      },
                    },
                  }}
                  className="player"
                />
              </div>
            )}
            {selectedVideo && (
              <div className="player-meta">
                <BlockTitle className="player-title">{selectedVideo.title}</BlockTitle>
                {selectedVideo.description && <p>{selectedVideo.description}</p>}
              </div>
            )}
          </div>
        </Page>
      </Popup>
    </Page>
  );
}

export default EducationalVideosList;
